import { computed, ref } from 'vue';
import { apiRequest } from '@/composables/useApi';
import { children as seedChildren, family, friends as seedFriends, messages, parents } from '@/data/seed';
import type { Child, Family, Friend, Message, MessageStatus, PlayRequest, PlayRequestStatus } from '@/types/domain';

interface NewChildInput {
  firstName: string;
  birthDate: string;
  avatar?: string;
  favoriteColor?: string;
  profileImageUrl: string | null;
  pin?: string;
}

interface UpdateChildInput {
  firstName: string;
  birthDate: string;
  avatar?: string;
  favoriteColor?: string;
  profileImageUrl: string | null;
  pin?: string;
  isActive?: boolean;
}

interface FriendProfileInput {
  childId: string;
  friendChildId?: string;
  friendFamilyId?: string | null;
  displayName: string;
  birthDate?: string | null;
  avatar: string;
  houseColor: string;
  invitationCode?: string | null;
  invitationLink?: string | null;
  qrCodePayload?: string | null;
  source?: Friend['source'];
}

export interface SenderInfo {
  name: string;
  imageUrl: string | null;
  color: string;
}

export type ChildReplyKind = 'wave' | 'thanks';

export interface PlayRequestOption {
  type: string;
  label: string;
  icon: string;
}

export interface PlayRequestInput {
  place: PlayRequestOption;
  time: PlayRequestOption;
}

type MediaUploadInput = {
  receiverChildId: string;
  type: 'drawing' | 'audio' | 'video';
  mediaKind: 'drawing' | 'photo' | 'audio' | 'video';
  file: Blob | string;
  filename: string;
  content: string;
  metadata: Record<string, unknown>;
};

const familyState = ref<Family>({ ...family });
const childrenState = ref<Child[]>([...seedChildren]);
const friendsState = ref<Friend[]>([...seedFriends]);
const selectedChildId = ref(seedChildren[0]?.id ?? '');
const selectedFriendId = ref('');
const mailbox = ref<Message[]>(messages);
const playRequests = ref<PlayRequest[]>([]);
const mailboxViewer = ref<'child' | 'parent'>('parent');
const drawingOccasion = ref<'birthday' | null>(null);
const celebratedBirthdayFriendIds = ref<string[]>([]);
const familyLoaded = ref(false);
const familyLoading = ref(false);
const familyError = ref('');
const loadedFamilyId = ref<string | null>(null);
let familyLoadRequestId = 0;

interface LoadFamilyOptions {
  force?: boolean;
}

export function useFamily() {
  const activeChildren = computed(() => childrenState.value.filter((child) => child.isActive !== false));

  const selectedChild = computed(
    () =>
      childrenState.value.find((child) => child.id === selectedChildId.value && child.isActive !== false) ??
      activeChildren.value[0] ??
      childrenState.value[0]
  );

  const childFriends = computed<Friend[]>(() =>
    selectedChild.value
      ? friendsState.value.filter((friend) => friend.childId === selectedChild.value.id && friend.status === 'active')
      : []
  );

  const selectedFriend = computed(
    () => childFriends.value.find((friend) => friend.id === selectedFriendId.value) ?? childFriends.value[0]
  );

  const pendingMessages = computed(() => mailbox.value.filter((message) => message.status === 'pending'));

  const pendingSenderParentPlayRequests = computed(() =>
    playRequests.value.filter((request) => request.status === 'pending_parent_approval')
  );

  const receiverParentPlayRequests = computed(() =>
    playRequests.value.filter((request) => request.status === 'sent_to_receiver_parent' || request.status === 'counter_proposal')
  );

  const confirmedPlayRequests = computed(() =>
    playRequests.value.filter((request) => request.status === 'confirmed')
  );

  const visibleChildPlayRequests = computed(() =>
    playRequests.value.filter(
      (request) =>
        request.senderChildId === selectedChild.value.id ||
        request.receiverChildId === selectedChild.value.id
    )
  );

  const childMailboxMessages = computed(() =>
    mailbox.value.filter(
      (message) =>
        message.senderChildId === selectedChild.value.id ||
        message.receiverChildId === selectedChild.value.id
    )
  );

  const childMailboxPlayRequests = computed(() => visibleChildPlayRequests.value);

  const receivedChildMessages = computed(() =>
    mailbox.value.filter(
      (message) =>
        message.receiverChildId === selectedChild.value.id &&
        (message.status === 'received' || message.status === 'approved')
    )
  );

  const unreadChildMessageCount = computed(
    () => receivedChildMessages.value.filter((message) => isUnreadChildMessage(message)).length
  );

  const isChildMailboxViewer = computed(() => mailboxViewer.value === 'child');

  async function loadFamilyData(expectedFamilyId?: string | null, options: LoadFamilyOptions = {}) {
    if (!options.force && familyLoaded.value && (!expectedFamilyId || loadedFamilyId.value === expectedFamilyId)) return;
    if (familyLoading.value) return;
    const requestId = ++familyLoadRequestId;
    familyLoading.value = true;
    familyError.value = '';
    try {
      const [familyResponse, childResponse, friendResponse, messageResponse, playRequestResponse] = await Promise.all([
        apiRequest<{ family: Family }>('/family'),
        apiRequest<{ children: Child[] }>('/children'),
        apiRequest<{ friends: Friend[] }>('/friends'),
        apiRequest<{ messages: Message[] }>('/messages'),
        apiRequest<{ playRequests: PlayRequest[] }>('/play-requests')
      ]);
      if (requestId !== familyLoadRequestId) return;
      if (expectedFamilyId && familyResponse.family.id !== expectedFamilyId) {
        throw new Error('family_mismatch');
      }
      familyState.value = familyResponse.family;
      childrenState.value = childResponse.children.map(normalizeChild);
      friendsState.value = friendResponse.friends.map(normalizeFriend);
      mailbox.value = messageResponse.messages;
      playRequests.value = playRequestResponse.playRequests;
      if (!childrenState.value.some((child) => child.id === selectedChildId.value && child.isActive !== false)) {
        selectedChildId.value = activeChildren.value[0]?.id ?? childrenState.value[0]?.id ?? '';
      }
      loadedFamilyId.value = familyResponse.family.id;
      familyLoaded.value = true;
    } catch {
      if (requestId !== familyLoadRequestId) return;
      familyError.value = 'Familiendaten konnten nicht geladen werden.';
    } finally {
      if (requestId === familyLoadRequestId) {
        familyLoading.value = false;
      }
    }
  }

  function resetFamilyData() {
    familyLoadRequestId++;
    familyState.value = { id: '', name: '', screenTimeMinutes: 25, createdAt: '' };
    childrenState.value = [];
    friendsState.value = [];
    selectedChildId.value = '';
    selectedFriendId.value = '';
    mailbox.value = [];
    playRequests.value = [];
    mailboxViewer.value = 'parent';
    drawingOccasion.value = null;
    celebratedBirthdayFriendIds.value = [];
    familyLoaded.value = false;
    familyLoading.value = false;
    familyError.value = '';
    loadedFamilyId.value = null;
  }

  function selectChild(child: Child) {
    if (child.isActive === false) return;
    selectedChildId.value = child.id;
    selectedFriendId.value = '';
  }

  function selectFriend(friend: Friend) {
    selectedFriendId.value = friend.id;
  }

  function startBirthdayDrawing(friend: Friend) {
    selectedFriendId.value = friend.id;
    drawingOccasion.value = 'birthday';
  }

  function selectFriendByChildId(friendChildId: string) {
    const friend = friendsState.value.find(
      (entry) =>
        entry.childId === selectedChild.value.id &&
        entry.friendChildId === friendChildId &&
        entry.status === 'active'
    );
    if (!friend) return false;

    selectedFriendId.value = friend.id;
    return true;
  }

  function setMailboxViewer(viewer: 'child' | 'parent') {
    mailboxViewer.value = viewer;
  }

  async function addChild(input: NewChildInput) {
    const colors = ['#fb7185', '#0ea5e9', '#22c55e', '#a855f7', '#f97316'];
    const favoriteColor = input.favoriteColor ?? colors[childrenState.value.length % colors.length];
    const avatar = input.avatar ?? 'heart-house';
    const payload = {
      firstName: input.firstName.trim(),
      birthDate: input.birthDate,
      avatar,
      favoriteColor,
      profileImageUrl: input.profileImageUrl,
      pin: input.pin || undefined
    };
    const response = await apiRequest<{ id: string }>('/children', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    const child: Child = {
      id: response.id,
      familyId: familyState.value.id,
      firstName: payload.firstName,
      birthDate: payload.birthDate,
      avatar,
      favoriteColor,
      profileImageUrl: input.profileImageUrl,
      pinEnabled: Boolean(input.pin),
      isActive: true
    };

    childrenState.value = [...childrenState.value, child];
    selectedChildId.value = child.id;
    return child;
  }

  async function updateFamilyName(name: string) {
    const trimmedName = name.trim();
    if (!trimmedName) return false;

    await apiRequest('/family', {
      method: 'PATCH',
      body: JSON.stringify({ name: trimmedName })
    });
    familyState.value = {
      ...familyState.value,
      name: trimmedName
    };
    return true;
  }

  async function updateChild(childId: string, input: UpdateChildInput) {
    const trimmedName = input.firstName.trim();
    if (!trimmedName || !input.birthDate) return false;

    await apiRequest(`/children/${encodeURIComponent(childId)}`, {
      method: 'PATCH',
      body: JSON.stringify({
        firstName: trimmedName,
        birthDate: input.birthDate,
        avatar: input.avatar,
        favoriteColor: input.favoriteColor,
        profileImageUrl: input.profileImageUrl,
        pin: input.pin || undefined,
        isActive: input.isActive
      })
    });

    let updated = false;
    childrenState.value = childrenState.value.map((child) => {
      if (child.id !== childId) return child;
      updated = true;
      return {
        ...child,
        firstName: trimmedName,
        birthDate: input.birthDate,
        avatar: input.avatar ?? child.avatar,
        favoriteColor: input.favoriteColor ?? child.favoriteColor,
        profileImageUrl: input.profileImageUrl,
        pinEnabled: input.pin ? true : child.pinEnabled,
        isActive: input.isActive ?? child.isActive
      };
    });

    return updated;
  }

  async function setChildActive(childId: string, isActive: boolean) {
    await apiRequest(`/children/${encodeURIComponent(childId)}/status`, {
      method: 'POST',
      body: JSON.stringify({ isActive })
    });

    const childExists = childrenState.value.some((child) => child.id === childId);
    if (!childExists) return false;

    childrenState.value = childrenState.value.map((child) =>
      child.id === childId ? { ...child, isActive } : child
    );

    if (!isActive && selectedChildId.value === childId) {
      selectedChildId.value = activeChildren.value[0]?.id ?? '';
      selectedFriendId.value = '';
    }

    return true;
  }

  async function removeChild(childId: string) {
    return setChildActive(childId, false);
  }

  function childInviteCode(child: Child) {
    return `HHKID:${familyState.value.id}:${child.id}:${encodeURIComponent(child.firstName)}`;
  }

  async function addManualFriend(input: FriendProfileInput) {
    const trimmedName = input.displayName.trim();
    if (!trimmedName || !input.childId) return null;

    const response = await apiRequest<{ id: string; friendChildId: string }>('/friends', {
      method: 'POST',
      body: JSON.stringify({
        childId: input.childId,
        friendChildId: input.friendChildId,
        friendFamilyId: input.friendFamilyId ?? null,
        displayName: trimmedName,
        birthDate: input.birthDate || null,
        avatar: input.avatar,
        houseColor: input.houseColor,
        invitationCode: input.invitationCode || null,
        invitationLink: input.invitationLink || null,
        qrCodePayload: input.qrCodePayload || null,
        source: input.source ?? 'manual'
      })
    });

    const friend = normalizeFriend({
      id: response.id,
      familyId: familyState.value.id,
      childId: input.childId,
      friendChildId: input.friendChildId || response.friendChildId,
      friendFamilyId: input.friendFamilyId ?? null,
      displayName: trimmedName,
      birthDate: input.birthDate || null,
      avatar: input.avatar,
      houseColor: input.houseColor,
      invitationCode: input.invitationCode || null,
      invitationLink: input.invitationLink || null,
      qrCodePayload: input.qrCodePayload || null,
      source: input.source ?? 'manual',
      status: 'active'
    });

    friendsState.value = [...friendsState.value, friend];
    return friend;
  }

  async function updateFriend(friendId: string, input: Omit<FriendProfileInput, 'childId'> & { status?: Friend['status'] }) {
    const trimmedName = input.displayName.trim();
    if (!trimmedName) return false;

    await apiRequest(`/friends/${encodeURIComponent(friendId)}`, {
      method: 'PATCH',
      body: JSON.stringify({
        displayName: trimmedName,
        birthDate: input.birthDate || null,
        avatar: input.avatar,
        houseColor: input.houseColor,
        invitationCode: input.invitationCode || null,
        invitationLink: input.invitationLink || null,
        qrCodePayload: input.qrCodePayload || null,
        source: input.source ?? 'manual',
        status: input.status
      })
    });

    let updated = false;
    friendsState.value = friendsState.value.map((friend) => {
      if (friend.id !== friendId) return friend;
      updated = true;
      return normalizeFriend({
        ...friend,
        displayName: trimmedName,
        birthDate: input.birthDate || null,
        avatar: input.avatar,
        houseColor: input.houseColor,
        invitationCode: input.invitationCode || null,
        invitationLink: input.invitationLink || null,
        qrCodePayload: input.qrCodePayload || null,
        source: input.source ?? 'manual',
        status: input.status ?? friend.status
      });
    });

    return updated;
  }

  async function setFriendActive(friendId: string, isActive: boolean) {
    await apiRequest(`/friends/${encodeURIComponent(friendId)}/status`, {
      method: 'POST',
      body: JSON.stringify({ isActive })
    });

    const childExists = friendsState.value.some((friend) => friend.id === friendId);
    if (!childExists) return false;

    friendsState.value = friendsState.value.map((friend) =>
      friend.id === friendId ? { ...friend, status: isActive ? 'active' : 'blocked' } : friend
    );

    if (!isActive && selectedFriendId.value === friendId) {
      selectedFriendId.value = '';
    }

    return true;
  }

  async function removeFriend(friendId: string) {
    return setFriendActive(friendId, false);
  }

  async function addFriendByInviteCode(childId: string, rawCode: string, fallbackName: string) {
    const ownChild = childrenState.value.find((child) => child.id === childId);
    const invite = parseInviteCode(rawCode);
    if (!ownChild || !invite || invite.childId === childId) return false;

    const exists = friendsState.value.some((friend) => friend.childId === childId && friend.friendChildId === invite.childId);
    if (exists) return true;

    const friend = await addManualFriend({
      childId,
      displayName: invite.firstName || fallbackName.trim() || 'Neuer Freund',
      friendChildId: invite.childId,
      friendFamilyId: invite.familyId ?? null,
      birthDate: null,
      avatar: 'house-parent-added',
      houseColor: '#38bdf8',
      invitationCode: rawCode.trim(),
      qrCodePayload: rawCode.trim(),
      source: invite.familyId ? 'qr' : 'invite_code'
    });
    if (!friend) return false;

    return true;
  }

  async function createPlayRequest(input: PlayRequestInput) {
    if (!selectedChild.value || !selectedFriend.value || !input.place || !input.time) return null;

    const now = new Date().toISOString();
    const history = [
      {
        id: crypto.randomUUID(),
        timestamp: now,
        actorType: 'child' as const,
        action: 'created_play_request',
        details: {
          place: input.place.label,
          time: input.time.label
        }
      }
    ];
    const response = await apiRequest<{ playRequest: PlayRequest }>('/play-requests', {
      method: 'POST',
      body: JSON.stringify({
        senderChildId: selectedChild.value.id,
        receiverChildId: selectedFriend.value.friendChildId,
        receiverFamilyId: 'receiver-family-placeholder',
        requestedPlaceType: input.place.type,
        requestedPlaceLabel: input.place.label,
        requestedTimeType: input.time.type,
        requestedTimeLabel: input.time.label,
        parentAdjustedPlace: input.place.label,
        parentMessage: `${selectedChild.value.firstName} moechte ${selectedFriend.value.displayName} zum Spielen fragen: ${input.place.label}, ${input.time.label}.`,
        history
      })
    });
    const request: PlayRequest = {
      ...response.playRequest,
      id: response.playRequest.id,
      senderChildId: selectedChild.value.id,
      receiverChildId: selectedFriend.value.friendChildId,
      senderFamilyId: familyState.value.id,
      receiverFamilyId: 'receiver-family-placeholder',
      requestedPlaceType: input.place.type,
      requestedPlaceLabel: input.place.label,
      requestedTimeType: input.time.type,
      requestedTimeLabel: input.time.label,
      parentAdjustedPlace: input.place.label,
      parentAdjustedDate: '',
      parentAdjustedTimeRange: '',
      parentAdjustedExactTime: '',
      parentMessage: `${selectedChild.value.firstName} moechte ${selectedFriend.value.displayName} zum Spielen fragen: ${input.place.label}, ${input.time.label}.`,
      parentCompanionNote: '',
      parentPhone: '',
      status: 'pending_parent_approval',
      createdAt: now,
      approvedBySenderParentAt: null,
      sentToReceiverParentAt: null,
      receiverParentRespondedAt: null,
      confirmedAt: null,
      declinedAt: null,
      counterProposal: null,
      history
    };

    playRequests.value = [request, ...playRequests.value];
    return request;
  }

  async function updatePlayRequestForParent(id: string, input: Partial<Pick<PlayRequest, 'parentAdjustedPlace' | 'parentAdjustedDate' | 'parentAdjustedTimeRange' | 'parentAdjustedExactTime' | 'parentMessage' | 'parentCompanionNote' | 'parentPhone'>>) {
    await apiRequest(`/play-requests/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input)
    });

    playRequests.value = playRequests.value.map((request) =>
      request.id === id
        ? {
            ...request,
            ...input,
            history: [
              {
                id: crypto.randomUUID(),
                timestamp: new Date().toISOString(),
                actorType: 'senderParent',
                action: 'adjusted_play_request',
                details: input
              },
              ...request.history
            ]
          }
        : request
    );
  }

  async function setPlayRequestStatus(id: string, status: PlayRequestStatus) {
    await apiRequest(`/play-requests/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status })
    });

    const now = new Date().toISOString();
    playRequests.value = playRequests.value.map((request) => {
      if (request.id !== id) return request;

      return {
        ...request,
        status,
        parentAdjustedDate:
          status === 'confirmed' && !request.parentAdjustedDate
            ? inferredPlayRequestDate(request.requestedTimeType)
            : request.parentAdjustedDate,
        approvedBySenderParentAt: status === 'sent_to_receiver_parent' ? now : request.approvedBySenderParentAt,
        sentToReceiverParentAt: status === 'sent_to_receiver_parent' ? now : request.sentToReceiverParentAt,
        receiverParentRespondedAt:
          status === 'confirmed' || status === 'declined' || status === 'counter_proposal'
            ? now
            : request.receiverParentRespondedAt,
        confirmedAt: status === 'confirmed' ? now : request.confirmedAt,
        declinedAt:
          status === 'declined' || status === 'rejected_by_sender_parent'
            ? now
            : request.declinedAt,
        history: [
          {
            id: crypto.randomUUID(),
            timestamp: now,
            actorType: 'senderParent',
            action: `status_${status}`,
            details: { status }
          },
          ...request.history
        ]
      };
    });
  }

  function playRequestChildStatusText(status: PlayRequestStatus, friendName: string) {
    if (status === 'pending_parent_approval') return 'Mama oder Papa schauen kurz drueber';
    if (status === 'sent_to_receiver_parent') return 'Deine Spielidee ist unterwegs';
    if (status === 'confirmed') return `${friendName} kann spielen!`;
    if (status === 'counter_proposal') return 'Die Eltern suchen noch eine gute Zeit';
    if (status === 'declined' || status === 'rejected_by_sender_parent') return 'Heute klappt es leider nicht';
    return 'Spielidee';
  }

  async function setMessageStatus(id: string, status: 'approved' | 'rejected') {
    const response = await apiRequest<{ message?: Message }>(status === 'approved' ? '/approve' : '/reject', {
      method: 'POST',
      body: JSON.stringify({ messageId: id })
    });

    const currentMessage = mailbox.value.find((message) => message.id === id);
    const updatedMessage = response.message;
    const nextStatus: MessageStatus =
      updatedMessage?.status ??
      (status === 'approved' && currentMessage?.metadata.sourceMessageId ? 'received' : status);
    const nextReceiverChildId = updatedMessage?.receiverChildId ?? currentMessage?.receiverChildId;
    if (nextStatus === 'received' && childrenState.value.some((child) => child.id === nextReceiverChildId)) {
      selectedChildId.value = nextReceiverChildId ?? selectedChildId.value;
      selectedFriendId.value = '';
    }

    mailbox.value = mailbox.value.map((message) =>
      message.id === id
        ? {
            ...message,
            ...updatedMessage,
            status: nextStatus,
            approvedBy: updatedMessage?.approvedBy ?? parents[0]?.id ?? null,
            approvedAt: updatedMessage?.approvedAt ?? new Date().toISOString(),
            metadata: {
              ...message.metadata,
              ...(updatedMessage?.metadata ?? {}),
              isNew: nextStatus === 'received' || status === 'approved' ? true : message.metadata.isNew
            }
          }
        : message
    );

    await loadFamilyData(loadedFamilyId.value ?? familyState.value.id, { force: true });
  }

  function markMessageRead(id: string) {
    mailbox.value = mailbox.value.map((message) =>
      message.id === id && message.receiverChildId === selectedChild.value.id
        ? {
            ...message,
            metadata: {
              ...message.metadata,
              isNew: false
            }
          }
        : message
    );
  }

  function messageSenderInfo(message: Message): SenderInfo {
    const familyChild = childrenState.value.find((child) => child.id === message.senderChildId);
    if (familyChild) {
      return {
        name: familyChild.firstName,
        imageUrl: familyChild.profileImageUrl,
        color: familyChild.favoriteColor
      };
    }

    const friend = friendsState.value.find(
      (entry) => entry.childId === selectedChild.value.id && entry.friendChildId === message.senderChildId
    );

    return {
      name: friend?.displayName ?? 'Freund',
      imageUrl: null,
      color: friend?.avatar.includes('blue') ? '#38bdf8' : friend?.avatar.includes('green') ? '#22c55e' : '#fb7185'
    };
  }

  function addDirectChildReaction(receiverChildId: string, replyKind: ChildReplyKind, sourceMessageId: string) {
    const receiverName =
      friendsState.value.find(
        (friend) => friend.childId === selectedChild.value.id && friend.friendChildId === receiverChildId
      )?.displayName ?? 'einen Freund';
    const content =
      replyKind === 'wave'
        ? `${selectedChild.value.firstName} winkt ${receiverName} zurueck.`
        : `${selectedChild.value.firstName} sagt ${receiverName} Danke.`;

    mailbox.value = [
      {
        id: crypto.randomUUID(),
        senderChildId: selectedChild.value.id,
        receiverChildId,
        familyId: familyState.value.id,
        type: 'kind_reply',
        content,
        mediaUrl: null,
        status: 'approved',
        createdAt: new Date().toISOString(),
        approvedBy: null,
        approvedAt: new Date().toISOString(),
        metadata: {
          source: 'child-mailbox-reaction',
          replyKind,
          sourceMessageId,
          receiverName,
          needsParentApproval: false
        }
      },
      ...mailbox.value
    ];
  }

  async function addPendingVoiceReply(receiverChildId: string, audio: Blob | string, sourceMessageId: string) {
    const receiverName =
      friendsState.value.find(
        (friend) => friend.childId === selectedChild.value.id && friend.friendChildId === receiverChildId
      )?.displayName ?? 'einen Freund';

    await createPendingMediaMessage({
      receiverChildId,
      type: 'audio',
      mediaKind: 'audio',
      file: audio,
      filename: 'voice-reply.webm',
      content: `${selectedChild.value.firstName} hat eine Sprachnachricht fuer ${receiverName} aufgenommen.`,
      metadata: {
        source: 'child-voice-reply',
        sourceMessageId,
        receiverName,
        maxDurationSeconds: 20
      }
    });
  }

  async function addPendingVoiceMessage(receiverChildId: string, audio: Blob | string) {
    const receiverName =
      selectedFriend.value?.friendChildId === receiverChildId
        ? selectedFriend.value.displayName
        : friendsState.value.find((friend) => friend.friendChildId === receiverChildId)?.displayName ?? 'einen Freund';

    await createPendingMediaMessage({
      receiverChildId,
      type: 'audio',
      mediaKind: 'audio',
      file: audio,
      filename: 'voice-message.webm',
      content: `${selectedChild.value.firstName} hat eine Sprachnachricht fuer ${receiverName} aufgenommen.`,
      metadata: {
        source: 'voice-pad',
        receiverName,
        maxDurationSeconds: 20
      }
    });
  }

  async function addPendingCameraMessage(receiverChildId: string, mediaKind: 'photo' | 'video', media: Blob | string, stickers: string[] = []) {
    const receiverName =
      selectedFriend.value?.friendChildId === receiverChildId
        ? selectedFriend.value.displayName
        : friendsState.value.find((friend) => friend.friendChildId === receiverChildId)?.displayName ?? 'einen Freund';
    const isPhoto = mediaKind === 'photo';

    await createPendingMediaMessage({
      receiverChildId,
      type: isPhoto ? 'drawing' : 'video',
      mediaKind,
      file: media,
      filename: isPhoto ? 'sticker-photo.png' : 'video-message.webm',
      content: isPhoto
        ? `${selectedChild.value.firstName} hat ein Sticker-Foto fuer ${receiverName} aufgenommen.`
        : `${selectedChild.value.firstName} hat ein kurzes Video fuer ${receiverName} aufgenommen.`,
      metadata: {
        source: 'camera-pad',
        mediaKind,
        receiverName,
        stickers,
        maxDurationSeconds: isPhoto ? undefined : 10
      }
    });
  }

  async function addPendingBirthdayMedia(receiverChildId: string, mediaType: 'audio' | 'video', media: Blob | string) {
    const receiverName =
      friendsState.value.find(
        (friend) => friend.childId === selectedChild.value.id && friend.friendChildId === receiverChildId
      )?.displayName ?? 'einen Freund';
    const mediaLabel = mediaType === 'audio' ? 'Geburtstags-Sprachnachricht' : 'Geburtstagsvideo';

    await createPendingMediaMessage({
      receiverChildId,
      type: mediaType,
      mediaKind: mediaType,
      file: media,
      filename: mediaType === 'audio' ? 'birthday-voice.webm' : 'birthday-video.webm',
      content: `${selectedChild.value.firstName} hat eine ${mediaLabel} fuer ${receiverName} aufgenommen.`,
      metadata: {
        source: 'birthday',
        receiverName,
        occasion: 'birthday',
        maxDurationSeconds: mediaType === 'audio' ? 20 : 10
      }
    });
    markBirthdayCelebrated(receiverChildId);
  }

  function markBirthdayCelebrated(friendChildId: string) {
    if (celebratedBirthdayFriendIds.value.includes(friendChildId)) return;
    celebratedBirthdayFriendIds.value = [...celebratedBirthdayFriendIds.value, friendChildId];
  }


  async function addPendingDrawing(receiverChildId: string, drawing: Blob | string) {
    const receiverName =
      selectedFriend.value?.friendChildId === receiverChildId
        ? selectedFriend.value.displayName
        : friendsState.value.find((friend) => friend.friendChildId === receiverChildId)?.displayName ?? 'einen Freund';

    const isBirthdayDrawing = drawingOccasion.value === 'birthday';

    await createPendingMediaMessage({
      receiverChildId,
      type: 'drawing',
      mediaKind: 'drawing',
      file: drawing,
      filename: isBirthdayDrawing ? 'birthday-drawing.png' : 'drawing.png',
      content: isBirthdayDrawing
        ? `${selectedChild.value.firstName} hat ein Geburtstagsbild fuer ${receiverName} gemalt.`
        : `${selectedChild.value.firstName} hat ein Bild fuer ${receiverName} gemalt.`,
      metadata: {
        source: isBirthdayDrawing ? 'birthday-drawing' : 'drawing-pad',
        receiverName,
        occasion: isBirthdayDrawing ? 'birthday' : undefined
      }
    });
    if (isBirthdayDrawing) {
      markBirthdayCelebrated(receiverChildId);
    }
    drawingOccasion.value = null;
  }

  async function createPendingMediaMessage(input: MediaUploadInput) {
    if (!selectedChild.value) return null;
    const blob = typeof input.file === 'string' ? await fetch(input.file).then((response) => response.blob()) : input.file;
    const formData = new FormData();
    formData.append('senderChildId', selectedChild.value.id);
    formData.append('receiverChildId', input.receiverChildId);
    formData.append('type', input.type);
    formData.append('mediaKind', input.mediaKind);
    formData.append('content', input.content);
    formData.append('metadata', JSON.stringify(input.metadata));
    formData.append('file', blob, input.filename);

    const response = await apiRequest<{ message: Message }>('/messages/media', {
      method: 'POST',
      body: formData
    });

    mailbox.value = [response.message, ...mailbox.value];
    return response.message;
  }

  return {
    addPendingDrawing,
    addPendingBirthdayMedia,
    addPendingCameraMessage,
    addDirectChildReaction,
    addPendingVoiceMessage,
    addPendingVoiceReply,
    addChild,
    addFriendByInviteCode,
    addManualFriend,
    childInviteCode,
    activeChildren,
    children: childrenState,
    childFriends,
    celebratedBirthdayFriendIds,
    childMailboxMessages,
    childMailboxPlayRequests,
    confirmedPlayRequests,
    family: familyState,
    familyError,
    familyLoaded,
    familyLoading,
    loadedFamilyId,
    friends: friendsState,
    mailbox,
    parents,
    pendingMessages,
    pendingSenderParentPlayRequests,
    playRequests,
    receiverParentPlayRequests,
    receivedChildMessages,
    visibleChildPlayRequests,
    isChildMailboxViewer,
    isUnreadChildMessage,
    drawingOccasion,
    selectedChild,
    selectedFriend,
    selectChild,
    selectFriend,
    selectFriendByChildId,
    loadFamilyData,
    resetFamilyData,
    startBirthdayDrawing,
    createPlayRequest,
    removeChild,
    removeFriend,
    setMailboxViewer,
    setChildActive,
    setFriendActive,
    unreadChildMessageCount,
    markMessageRead,
    messageSenderInfo,
    playRequestChildStatusText,
    setMessageStatus,
    setPlayRequestStatus,
    updatePlayRequestForParent,
    updateChild,
    updateFriend,
    updateFamilyName
  };
}

function inferredPlayRequestDate(requestedTimeType: string) {
  const today = new Date();
  if (requestedTimeType === 'today') return dateKey(today);
  if (requestedTimeType === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    return dateKey(tomorrow);
  }
  if (requestedTimeType === 'weekend') {
    const weekend = new Date(today);
    const daysUntilSaturday = (6 - today.getDay() + 7) % 7 || 7;
    weekend.setDate(today.getDate() + daysUntilSaturday);
    return dateKey(weekend);
  }
  return '';
}

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function isUnreadChildMessage(message: Message) {
  return (
    (message.status === 'received' || message.status === 'approved') &&
    message.metadata.isNew !== false
  );
}

function parseInviteCode(rawCode: string) {
  const trimmed = rawCode.trim();
  const directChildId = trimmed.match(/^(child-[a-zA-Z0-9-]+)$/);
  if (directChildId) return { childId: directChildId[1], firstName: '' };

  const parts = trimmed.split(':');
  if (parts[0] !== 'HHKID' || parts.length < 4) return null;

  return {
    familyId: parts[1],
    childId: parts[2],
    firstName: decodeURIComponent(parts.slice(3).join(':'))
  };
}

function normalizeChild(child: Child & { isActive?: boolean | number }): Child {
  const activeValue = child.isActive as boolean | number | undefined;
  return {
    ...child,
    birthDate: child.birthDate ?? '',
    profileImageUrl: child.profileImageUrl ?? null,
    pinEnabled: Boolean(child.pinEnabled),
    isActive: activeValue !== false && activeValue !== 0
  };
}

function normalizeFriend(friend: Friend): Friend {
  return {
    ...friend,
    houseColor: friend.houseColor ?? '#38bdf8',
    birthDate: friend.birthDate ?? null,
    invitationCode: friend.invitationCode ?? null,
    invitationLink: friend.invitationLink ?? null,
    qrCodePayload: friend.qrCodePayload ?? null,
    source: friend.source ?? 'manual',
    status: friend.status ?? 'active'
  };
}
